import React, { useState, useEffect, useRef } from "react";
import {
  Box,
  Flex,
  Heading,
  VStack,
  HStack,
  useColorMode,
  IconButton,
  Button,
  Menu as ChakraMenu,
  MenuButton,
  MenuList,
  MenuItem,
  Text,
  Image,
  usePrefersReducedMotion,
  Container,
} from "@chakra-ui/react";
import { keyframes, css } from "@emotion/react";
import {
  SunIcon,
  MoonIcon,
  HamburgerIcon,
  CloseIcon,
  ChevronDownIcon,
} from "@chakra-ui/icons";
import { MdLanguage } from "react-icons/md";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Language, languageNames } from "../i18n/i18n";
import { useAuthStore } from "../store/authStore";
import { useLanguageStore } from "../store/languageStore";
import Avatar from "./Avatar";

const LanguageMenuGlyph = MdLanguage as React.FC<{ size?: number }>;

// Define menu items
export type MenuItem = {
  id: string;
  label: string;
  path: string;
  disabled?: boolean;
};

export const menuItems: MenuItem[] = [
  { id: "library", label: "library", path: "/" },
  { id: "document", label: "document", path: "/document" },
  { id: "about", label: "about", path: "/about" },
];

export const booksSubItems: MenuItem[] = [
  { id: "myBooks", label: "myBooks", path: "/my-books" },
  { id: "createBook", label: "createBook", path: "/create" },
];

// Main Menu component
const Menu = () => {
  const { colorMode, toggleColorMode } = useColorMode();
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState<boolean>(false);
  const mobileToolbarRef = useRef<HTMLDivElement>(null);
  const mobileMenuPanelRef = useRef<HTMLDivElement>(null);
  const [isScrolled, setIsScrolled] = useState<boolean>(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const prefersReducedMotion = usePrefersReducedMotion();
  const { isAuthenticated, username, firstName, lastName, logout, checkAuth } =
    useAuthStore();

  const initials = React.useMemo(() => {
    if (!username) return "?";
    const parts = username.split(/[@\\s._-]+/).filter(Boolean);
    const letters = parts.slice(0, 2).map((p) => p[0]?.toUpperCase() || "");
    const joined = letters.join("");
    return joined || username[0]?.toUpperCase() || "?";
  }, [username]);

  // Check auth status on mount
  React.useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  // Define animation keyframes
  const growFromCenter = keyframes`
    0% {
      clip-path: circle(0% at 50% 50%);
      opacity: 0.3;
    }
    30% {
      clip-path: circle(20% at 50% 50%);
      opacity: 0.7;
    }
    60% {
      clip-path: circle(100% at 50% 50%);
      opacity: 1;
    }
    80% {
      clip-path: circle(100% at 50% 50%);
      opacity: 1;
    }
    100% {
      clip-path: circle(0% at 50% 50%);
      opacity: 0.3;
    }
  `;

  const fadeOut = keyframes`
    0% {
      opacity: 0.8;
    }
    100% {
      opacity: 0;
    }
  `;

  const rotate = keyframes`
    from { transform: rotate(0deg); }
    to { transform: rotate(360deg); }
  `;

  const pulse = keyframes`
    0% { transform: scale(0.95); opacity: 0.8; }
    50% { transform: scale(1.05); opacity: 1; }
    100% { transform: scale(0.95); opacity: 0.8; }
  `;

  const shimmer = keyframes`
    0% { filter: brightness(1) hue-rotate(0deg); }
    50% { filter: brightness(1.2) hue-rotate(15deg); }
    100% { filter: brightness(1) hue-rotate(0deg); }
  `;

  // Add scroll event listener to detect scrolling
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 0);
    };

    window.addEventListener("scroll", handleScroll);
    return () => {
      window.removeEventListener("scroll", handleScroll);
    };
  }, []);

  const setLanguageFromStore = useLanguageStore((s) => s.setLanguage);

  // Ensure i18n is properly initialized (persist normalized choice via store)
  useEffect(() => {
    const currentLng = i18n.language || "en";
    const isFullySupported = Object.values(Language).includes(
      currentLng as Language,
    );

    if (!isFullySupported) {
      if (currentLng.startsWith("kk")) {
        if (currentLng.includes("latin") || currentLng.includes("latn")) {
          setLanguageFromStore(Language.KAZAKH_LATIN);
        } else {
          setLanguageFromStore(Language.KAZAKH_CYRILLIC);
        }
      } else if (currentLng.startsWith("ru")) {
        setLanguageFromStore(Language.RUSSIAN);
      } else {
        setLanguageFromStore(Language.ENGLISH);
      }
    }
  }, [setLanguageFromStore]);

  // Get active menu based on current path
  const getActiveMenu = () => {
    const path = location.pathname;
    if (path.startsWith("/read/")) return null; // No active menu on read page
    if (path === "/my-books" || path === "/create") return "books";
    return menuItems.find((item) => item.path === path)?.id || "library";
  };

  const activeMenu = getActiveMenu();

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  useEffect(() => {
    if (!isMobileMenuOpen) return;

    const handlePointerDown = (event: PointerEvent) => {
      const target = event.target as Node;
      if (
        mobileToolbarRef.current?.contains(target) ||
        mobileMenuPanelRef.current?.contains(target)
      ) {
        return;
      }
      setIsMobileMenuOpen(false);
    };

    document.addEventListener("pointerdown", handlePointerDown);
    return () => document.removeEventListener("pointerdown", handlePointerDown);
  }, [isMobileMenuOpen]);

  // Handle language change (persisted via languageStore)
  const changeLanguage = (language: Language) => {
    setIsScrolled(window.scrollY > 0);
    setLanguageFromStore(language).then(() => {
      if (i18n.language !== language) {
        setTimeout(() => window.location.reload(), 100);
      } else {
        document.dispatchEvent(new Event("languageChanged"));
      }
    });
  };

  // Get current language display name
  const getCurrentLanguageName = () => {
    // Get current language with fallback
    const currentLang = i18n.language || Language.ENGLISH;

    // Handle capitalization difference (kk-Latn vs kk-latn)
    const normalizedLang =
      currentLang === "kk-Latn" ? Language.KAZAKH_LATIN : currentLang;

    // Check if the normalized language is one of our supported languages
    const isSupported = Object.values(Language).includes(
      normalizedLang as Language,
    );

    // If not supported, provide a fallback
    if (!isSupported) {
      // For browsers that return variations of Qazaq
      if (normalizedLang.startsWith("kk")) {
        // If it contains 'latin' or 'latn', use the Latin version (case insensitive check)
        if (
          normalizedLang.toLowerCase().includes("latin") ||
          normalizedLang.toLowerCase().includes("latn")
        ) {
          return languageNames[Language.KAZAKH_LATIN];
        }
        // Otherwise default to Cyrillic
        return languageNames[Language.KAZAKH_CYRILLIC];
      }
      return languageNames[Language.ENGLISH];
    }

    return languageNames[normalizedLang as Language];
  };

  return (
    <Box
      id="main-menu"
      position="sticky"
      top={isScrolled ? "10px" : 0}
      paddingTop={0}
      zIndex={100}
      width="100%"
      mb={6}
      transition="all 0.2s ease"
      bg="transparent"
      boxShadow="none"
      px={{ base: 0, md: "inherit" }}
    >
      <Flex
        id="main-menu-wrapper"
        direction="column"
        w="100%"
        overflow="hidden"
        minW={0}
        bg={
          colorMode === "light"
            ? isScrolled
              ? "white"
              : "transparent"
            : "#1A202C" /* Solid background color for dark mode */
        }
        p={{ base: 2, md: 4 }}
        borderRadius="md"
        boxShadow={isScrolled ? "md" : "none"}
        transition="all 0.2s ease"
      >
        <Flex
          id="main-menu-header"
          as="header"
          width="100%"
          minW={0}
          align="center"
          justify="space-between"
        >
          <Flex
            id="main-menu-brand"
            align="center"
            cursor="pointer"
            onClick={() => navigate("/")}
            mr={6}
          >
            <Box
              id="main-menu-logo-box"
              position="relative"
              mr={2}
              width="40px"
              height="40px"
              css={css`
                position: relative;
                overflow: hidden;

                & img {
                  transform-origin: center;
                }

                &:hover img {
                  animation: ${!prefersReducedMotion
                    ? css`
                        ${rotate} 1s linear
                      `
                    : "none"};
                }
              `}
            >
              {/* Main Logo */}
              <Image
                id="main-menu-logo"
                src="/logo.svg"
                alt="Logo"
                boxSize="40px"
                position="absolute"
                top="0"
                left="0"
                zIndex="1"
                css={
                  !prefersReducedMotion &&
                  css`
                    animation:
                      ${growFromCenter} 7s ease-in-out infinite,
                      ${shimmer} 3s ease-in-out infinite;
                  `
                }
              />

              {/* Echo effect */}
              <Image
                id="main-menu-logo-echo"
                src="/logo.svg"
                alt=""
                aria-hidden="true"
                boxSize="40px"
                position="absolute"
                top="0"
                left="0"
                opacity="0.6"
                zIndex="0"
                css={
                  !prefersReducedMotion &&
                  css`
                    animation:
                      ${growFromCenter} 7s ease-in-out infinite,
                      ${pulse} 3s ease-in-out infinite;
                    animation-delay: 0.7s;
                  `
                }
              />
            </Box>
            <Heading id="main-menu-app-name" size="lg">
              {t("app.name")}
            </Heading>
          </Flex>

          {/* Desktop Menu (show on xl and up; collapsed below) */}
          <HStack
            id="main-menu-desktop"
            spacing={4}
            ml={2}
            minW={0}
            flex="1"
            justify="flex-end"
            display={{ base: "none", xl: "flex" }}
          >
            {menuItems.map((item) => (
              <React.Fragment key={item.id}>
                <Button
                  id={`menu-item-${item.id}`}
                  as={item.disabled ? undefined : Link}
                  to={item.disabled ? undefined : item.path}
                  onClick={
                    item.disabled ? (e) => e.preventDefault() : undefined
                  }
                  variant={activeMenu === item.id ? "solid" : "ghost"}
                  _hover={{
                    transform:
                      activeMenu === item.id || item.disabled
                        ? "none"
                        : "translateY(-2px)",
                    bg: activeMenu === item.id ? undefined : "transparent",
                  }}
                  transition="all 0.2s"
                  opacity={item.disabled ? 0.6 : 1}
                  cursor={item.disabled ? "not-allowed" : "pointer"}
                  isDisabled={item.disabled}
                >
                  {t(`menu.${item.label}`)}
                </Button>
                {item.id === "library" && (
                  <ChakraMenu id="main-menu-books-desktop">
                    <MenuButton
                      id="menu-button-books-desktop"
                      as={Button}
                      variant={activeMenu === "books" ? "solid" : "ghost"}
                      _hover={{ bg: "transparent" }}
                      mr={2}
                    >
                      <HStack spacing={2} as="span">
                        <Text as="span">{t("menu.books")}</Text>
                        <ChevronDownIcon
                          id="menu-button-books-desktop-icon"
                          boxSize={4}
                        />
                      </HStack>
                    </MenuButton>
                    <MenuList id="books-menu-list-desktop">
                      {booksSubItems.map((subItem) => (
                        <MenuItem
                          key={subItem.id}
                          id={`books-menu-item-desktop-${subItem.id}`}
                          as={Link}
                          to={subItem.path}
                        >
                          {t(`menu.${subItem.label}`)}
                        </MenuItem>
                      ))}
                    </MenuList>
                  </ChakraMenu>
                )}
              </React.Fragment>
            ))}

            {!isAuthenticated && (
              <Button
                id="login-button-desktop"
                as={Link}
                to="/login"
                variant={location.pathname === "/login" ? "solid" : "ghost"}
                _hover={{
                  transform: "translateY(-2px)",
                  bg:
                    location.pathname === "/login" ? undefined : "transparent",
                }}
                transition="all 0.2s"
              >
                {t("menu.login") || "Login"}
              </Button>
            )}
            {/* Language Selector */}
            <ChakraMenu id="main-menu-language-desktop">
              <MenuButton
                id="language-menu-button-desktop"
                as={Button}
                variant="ghost"
                _hover={{ bg: "transparent" }}
                mr={2}
                aria-label={`${t("menu.language")}: ${getCurrentLanguageName()}`}
              >
                <HStack spacing={2} as="span" align="center">
                  <Box as="span" display="inline-flex" aria-hidden>
                    <LanguageMenuGlyph size={20} />
                  </Box>
                  <Text as="span">{getCurrentLanguageName()}</Text>
                  <ChevronDownIcon
                    id="language-menu-button-desktop-icon"
                    boxSize={4}
                  />
                </HStack>
              </MenuButton>
              <MenuList id="language-menu-list-desktop">
                {Object.values(Language).map((lang) => {
                  // Check if this language is currently active
                  const isActive = (() => {
                    if (i18n.language === lang) return true;

                    // Handle capitalization variations
                    if (
                      i18n.language === "kk-Latn" &&
                      lang === Language.KAZAKH_LATIN
                    )
                      return true;

                    // Handle partial matches and variations
                    if (lang === Language.KAZAKH_CYRILLIC) {
                      // Match if language starts with kk but is not specifically kk-latn or kk-Latn
                      return (
                        i18n.language?.startsWith("kk") &&
                        !i18n.language.toLowerCase().includes("latn")
                      );
                    }
                    if (lang === Language.KAZAKH_LATIN) {
                      // Match if language starts with kk and includes latn or latin (case insensitive)
                      return (
                        i18n.language?.startsWith("kk") &&
                        (i18n.language.toLowerCase().includes("latn") ||
                          i18n.language.toLowerCase().includes("latin"))
                      );
                    }
                    return false;
                  })();

                  return (
                    <MenuItem
                      key={lang}
                      id={`language-option-desktop-${lang}`}
                      onClick={() => {
                        changeLanguage(lang);
                        // Force re-render immediately
                        window.setTimeout(
                          () => setIsScrolled(window.scrollY > 0),
                          50,
                        );
                      }}
                      fontWeight={isActive ? "bold" : "normal"}
                      bg={isActive ? "rgba(0, 0, 0, 0.05)" : "transparent"}
                      _hover={{
                        bg:
                          colorMode === "light"
                            ? "rgba(0, 0, 0, 0.08)"
                            : "rgba(255, 255, 255, 0.08)",
                        transform: "translateX(2px)",
                      }}
                      transition="all 0.2s"
                    >
                      {languageNames[lang]}
                    </MenuItem>
                  );
                })}
              </MenuList>
            </ChakraMenu>

            <IconButton
              id="color-toggle-desktop"
              aria-label="Toggle color mode"
              icon={colorMode === "light" ? <MoonIcon /> : <SunIcon />}
              onClick={toggleColorMode}
              size="md"
              variant="ghost"
              flexShrink={0}
            />
            {isAuthenticated && (
              <Avatar
                id="user-avatar-desktop"
                username={username || ""}
                firstName={firstName}
                lastName={lastName}
                onLogout={() => {
                  logout();
                  navigate("/");
                }}
                size={36}
              />
            )}
          </HStack>

          {/* Mobile/Tablet Menu Toggle and User Avatar (below xl) */}
          <HStack
            id="main-menu-mobile-toolbar"
            ref={mobileToolbarRef}
            display={{ base: "flex", xl: "none" }}
            spacing={3}
          >
            {isAuthenticated && (
              <Avatar
                username={username || ""}
                firstName={firstName}
                lastName={lastName}
                onLogout={() => {
                  logout();
                  navigate("/");
                }}
                size={32}
                id="user-avatar"
              />
            )}
            <IconButton
              id="menu-toggle-mobile"
              aria-label="Toggle menu"
              icon={isMobileMenuOpen ? <CloseIcon /> : <HamburgerIcon />}
              onClick={toggleMobileMenu}
              size="md"
              variant="ghost"
            />
          </HStack>
        </Flex>

        {/* Mobile Menu */}
        {isMobileMenuOpen && (
          <VStack
            id="main-menu-mobile-panel"
            ref={mobileMenuPanelRef}
            display={{ base: "flex", xl: "none" }}
            width="100%"
            mt={4}
            spacing={4}
            align="flex-start"
            bg={colorMode === "light" ? "white" : "#1A202C"}
            p={3}
            borderRadius="md"
            boxShadow="sm"
          >
            {menuItems.map((item) => (
              <React.Fragment key={item.id}>
                <Button
                  id={`menu-item-mobile-${item.id}`}
                  as={item.disabled ? undefined : Link}
                  to={item.disabled ? undefined : item.path}
                  onClick={
                    item.disabled
                      ? (e) => e.preventDefault()
                      : () => setIsMobileMenuOpen(false)
                  }
                  variant={activeMenu === item.id ? "solid" : "ghost"}
                  justifyContent="flex-start"
                  width="100%"
                  height="auto"
                  py={2}
                  borderRadius="md"
                  _hover={{
                    bg: "transparent",
                    transform: item.disabled ? "none" : "translateX(2px)",
                  }}
                  transition="all 0.2s"
                  opacity={item.disabled ? 0.6 : 1}
                  cursor={item.disabled ? "not-allowed" : "pointer"}
                  isDisabled={item.disabled}
                >
                  {t(`menu.${item.label}`)}
                </Button>
                {item.id === "library" &&
                  booksSubItems.map((subItem) => (
                    <Button
                      key={subItem.id}
                      id={`menu-item-mobile-${subItem.id}`}
                      as={Link}
                      to={subItem.path}
                      onClick={() => setIsMobileMenuOpen(false)}
                      variant={
                        location.pathname === subItem.path ? "solid" : "ghost"
                      }
                      justifyContent="flex-start"
                      width="100%"
                      height="auto"
                      py={2}
                      borderRadius="md"
                      _hover={{
                        bg: "transparent",
                        transform: "translateX(2px)",
                      }}
                      transition="all 0.2s"
                    >
                      {t(`menu.${subItem.label}`)}
                    </Button>
                  ))}
              </React.Fragment>
            ))}

            {/* Mobile Login/Logout Button */}
            {!isAuthenticated && (
              <Button
                id="login-button-mobile"
                as={Link}
                to="/login"
                variant={location.pathname === "/login" ? "solid" : "ghost"}
                justifyContent="flex-start"
                width="100%"
                height="auto"
                py={2}
                borderRadius="md"
                onClick={() => setIsMobileMenuOpen(false)}
                _hover={{
                  bg: "transparent",
                  transform: "translateX(2px)",
                }}
                transition="all 0.2s"
              >
                {t("menu.login") || "Login"}
              </Button>
            )}

            {/* Mobile Language Selector */}
            <ChakraMenu id="main-menu-language-mobile" placement="bottom">
              <MenuButton
                id="language-menu-button-mobile"
                as={Button}
                leftIcon={
                  <Box
                    as="span"
                    display="inline-flex"
                    alignItems="center"
                    aria-hidden
                  >
                    <LanguageMenuGlyph size={20} />
                  </Box>
                }
                rightIcon={<ChevronDownIcon />}
                variant="ghost"
                textAlign="left"
                width="100%"
                justifyContent="flex-start"
                height="auto"
                py={2}
                borderRadius="md"
                aria-label={`${t("menu.language")}: ${getCurrentLanguageName()}`}
              >
                {getCurrentLanguageName()}
              </MenuButton>
              <MenuList id="language-menu-list-mobile">
                {Object.values(Language).map((lang) => {
                  // Check if this language is currently active
                  const isActive = (() => {
                    if (i18n.language === lang) return true;

                    // Handle capitalization variations
                    if (
                      i18n.language === "kk-Latn" &&
                      lang === Language.KAZAKH_LATIN
                    )
                      return true;

                    // Handle partial matches and variations
                    if (lang === Language.KAZAKH_CYRILLIC) {
                      // Match if language starts with kk but is not specifically kk-latn or kk-Latn
                      return (
                        i18n.language?.startsWith("kk") &&
                        !i18n.language.toLowerCase().includes("latn")
                      );
                    }
                    if (lang === Language.KAZAKH_LATIN) {
                      // Match if language starts with kk and includes latn or latin (case insensitive)
                      return (
                        i18n.language?.startsWith("kk") &&
                        (i18n.language.toLowerCase().includes("latn") ||
                          i18n.language.toLowerCase().includes("latin"))
                      );
                    }
                    return false;
                  })();

                  return (
                    <MenuItem
                      key={lang}
                      id={`language-option-mobile-${lang}`}
                      onClick={() => {
                        changeLanguage(lang);
                        setIsMobileMenuOpen(false);
                        // Force re-render immediately
                        window.setTimeout(
                          () => setIsScrolled(window.scrollY > 0),
                          50,
                        );
                      }}
                      fontWeight={isActive ? "bold" : "normal"}
                      bg={isActive ? "rgba(0, 0, 0, 0.05)" : "transparent"}
                      _hover={{
                        bg:
                          colorMode === "light"
                            ? "rgba(0, 0, 0, 0.08)"
                            : "rgba(255, 255, 255, 0.08)",
                        transform: "translateX(2px)",
                      }}
                      transition="all 0.2s"
                    >
                      {languageNames[lang]}
                    </MenuItem>
                  );
                })}
              </MenuList>
            </ChakraMenu>

            <Button
              id="color-toggle-mobile"
              aria-label="Toggle color mode"
              leftIcon={colorMode === "light" ? <MoonIcon /> : <SunIcon />}
              onClick={toggleColorMode}
              size="md"
              variant="ghost"
              alignSelf="flex-start"
              width="100%"
              justifyContent="flex-start"
              height="auto"
              py={2}
              borderRadius="md"
            >
              {colorMode === "light"
                ? t("readBook.darkMode")
                : t("readBook.lightMode")}
            </Button>
          </VStack>
        )}
      </Flex>
    </Box>
  );
};

export default Menu;
